// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data: the browser's calls to the site's `/api/data/` routes
 * (docs/AYNI_DATA_INTAKE_API.md §12), except the upload itself
 * (`ownDataUpload.ts`, in a worker). Every call carries the session
 * cookie (same origin) and answers `{ ok: true, value }` or
 * `{ ok: false, status, error }` with the route's error code, so the UI
 * can say what happened in plain words. Nothing here is cached: roles and
 * plans can change between two calls (a lab's owner, a lapsed licence).
 */

import type { JobChoices, JobState } from './ownData'
import type { LabRole, StepUpAction } from './ownDataSharing'
import type { SourceAlertKind, SourceSchedule, SourceState, SourceWindow } from './ownDataSources'
import type { CreationJson, RequestJson } from './passkeyClient'

export type ApiResult<T> = { ok: true; value: T } | { ok: false; status: number; error: string; retryAfterS?: number; body?: Record<string, unknown> }

export type TenantRole = 'owner' | 'member' | 'viewer'

/** An intake report variable (docs §8; only `name` is guaranteed). */
export interface ReportVariable {
  name: string
  longName?: string
  units?: string
  dims?: string[]
  shape?: number[]
  dtype?: string
  fillValue?: number | null
  percentiles?: { p0_1?: number; p99_9?: number }
}

export interface IntakeReport {
  format?: string
  conventions?: string
  variables: ReportVariable[]
  grid?: {
    type?: string
    lat?: { n?: number; min?: number; max?: number }
    lon?: { n?: number; min?: number; max?: number }
    crs?: string
  }
  time?: { steps?: number; start?: string; end?: string; cadenceSeconds?: number; calendar?: string }
  warnings?: string[]
  estimate?: { outputBytes?: number; minutes?: number }
}

export interface JobView {
  id: string
  dataset: string
  state: JobState
  fileName: string
  bytes: number
  parts: number
  step: string | null
  progress: number | null
  sha256: { browser: string | null; server: string | null }
  detectedType: string | null
  report: IntakeReport | null
  choices: Partial<JobChoices> | null
  release: string | null
  budget: Record<string, unknown> | null
  tolerancePass: boolean | null
  error: { code: string; detail: string | null } | null
  createdAt: number
  updatedAt: number
}

export interface DatasetView {
  id: string
  title: string
  state: 'pending' | 'ready' | 'deleting' | 'deleted'
  mine: boolean
  createdAt: number
  bytes: number
  release: string | null
  base: string
  latest: string | null
  job: JobView | null
  /** Phase 4: the open licence once published openly; how many shares are active. */
  open?: string | null
  shares?: number
  /** Phase 3: the continuous source feeding it. */
  source?: string | null
}

export interface DatasetList {
  tenant: { id: string; kind: string; state: string; role: TenantRole; readOnly: boolean } | null
  quota: { storedBytes: number; jobBytes: number; usedBytes: number } | null
  datasets: DatasetView[]
}

export interface DataToken {
  token: string
  expiresAt: number
  base: string
  latest: string
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

async function call<T>(method: string, path: string, body?: unknown, fetchImpl: typeof fetch = fetch): Promise<ApiResult<T>> {
  let res: Response
  try {
    res = await fetchImpl(path, {
      method,
      headers: body === undefined ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      cache: 'no-store',
    })
  } catch {
    return { ok: false, status: 0, error: 'unreachable' }
  }
  const json = (await res.json().catch(() => null)) as unknown
  if (res.ok && isRecord(json)) return { ok: true, value: json as T }
  const retry = Number(res.headers.get('Retry-After'))
  return {
    ok: false,
    status: res.status,
    error: isRecord(json) && typeof json.error === 'string' ? json.error : `http_${res.status}`,
    ...(Number.isFinite(retry) && retry > 0 ? { retryAfterS: retry } : {}),
    ...(isRecord(json) ? { body: json } : {}),
  }
}

const enc = encodeURIComponent

export const listOwnDatasets = (f?: typeof fetch) => call<DatasetList>('GET', '/api/data/datasets', undefined, f)
export const getOwnDataset = (id: string, f?: typeof fetch) => call<{ dataset: DatasetView }>('GET', `/api/data/datasets/${enc(id)}`, undefined, f)
export const getJob = (id: string, f?: typeof fetch) => call<{ job: JobView }>('GET', `/api/data/jobs/${enc(id)}`, undefined, f)
export const submitChoices = (id: string, choices: JobChoices, f?: typeof fetch) => call<{ job: JobView }>('POST', `/api/data/jobs/${enc(id)}/choices`, choices, f)
export const publishJob = (id: string, f?: typeof fetch) => call<{ job: JobView }>('POST', `/api/data/jobs/${enc(id)}/publish`, {}, f)
/** Cancel an unfinished upload (the uploader's own, or any for the lab's owner): its dataset is crypto-shredded unless it has a published release. */
export const cancelOwnUpload = (job: string, f?: typeof fetch) => call<{ job: JobView; datasetShredded: boolean }>('POST', `/api/data/uploads/${enc(job)}/cancel`, {}, f)
export const deleteOwnDataset = (id: string, f?: typeof fetch) => call<{ deleted: boolean; keyId: string }>('DELETE', `/api/data/datasets/${enc(id)}`, undefined, f)
/** A token for a dataset: the lab's own, one shared with this account, or (with `meeting`, a room code) one shown to the meeting this browser is in. */
export const datasetToken = (id: string, f?: typeof fetch, meeting?: string | null) => call<DataToken>('POST', `/api/data/datasets/${enc(id)}/token`, meeting ? { meeting } : {}, f)

/** The custody record as a file to save (owner: `scope=tenant` gives the whole lab's chain). */
export async function custodyRecord(id: string, scope: 'dataset' | 'tenant' = 'dataset', fetchImpl: typeof fetch = fetch): Promise<ApiResult<Blob>> {
  let res: Response
  try {
    res = await fetchImpl(`/api/data/custody/${enc(id)}${scope === 'tenant' ? '?scope=tenant' : ''}`, { credentials: 'same-origin', cache: 'no-store' })
  } catch {
    return { ok: false, status: 0, error: 'unreachable' }
  }
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as unknown
    return { ok: false, status: res.status, error: isRecord(json) && typeof json.error === 'string' ? json.error : `http_${res.status}` }
  }
  return { ok: true, value: new Blob([await res.arrayBuffer()], { type: 'application/json' }) }
}

// ------------------------------------------------------- phase 4: sharing

export interface ShareView {
  id: string
  kind: 'account' | 'link'
  email: string | null
  seen: boolean | null
  accepted: number | null
  state: 'active' | 'expired' | 'revoked'
  createdAt: number
  expiresAt: number
  revokedAt: number | null
  mine: boolean
}

export interface SharedDataset {
  id: string
  title: string
  via: 'invite' | 'link'
  sharedBy: string | null
  expiresAt: number
  release: string | null
  base: string
  latest: string
}

export interface DataFeatures {
  stepUp: boolean
  meetingShares: boolean
  openPublishing: boolean
}

export interface MemberView {
  account: string
  email: string | null
  name: string | null
  role: LabRole
  addedAt: number
  lastSeenHolding: number | null
  you: boolean
}

export const sharedWithMe = (f?: typeof fetch) => call<{ datasets: SharedDataset[]; features: DataFeatures }>('GET', '/api/data/shared', undefined, f)
export const listShares = (dataset: string, f?: typeof fetch) => call<{ shares: ShareView[] }>('GET', `/api/data/datasets/${enc(dataset)}/shares`, undefined, f)
export const createShare = (dataset: string, body: { kind: 'account'; email: string; days?: number } | { kind: 'link'; days?: number }, f?: typeof fetch) =>
  call<{ share: ShareView; link?: string; mailed?: boolean }>('POST', `/api/data/datasets/${enc(dataset)}/shares`, body, f)
export const revokeShare = (share: string, f?: typeof fetch) => call<{ revoked: boolean }>('DELETE', `/api/data/shares/${enc(share)}`, undefined, f)
export const acceptShareLink = (link: string, f?: typeof fetch) => call<{ dataset: { id: string; title: string }; expiresAt: number }>('POST', '/api/data/shares/accept', { link }, f)
/** What a share link opens, without accepting it (the page asks first). */
export const previewShareLink = (link: string, f?: typeof fetch) =>
  call<{ dataset: { id: string; title: string }; sharedBy: string | null; expiresAt: number; ownLab: boolean }>('POST', '/api/data/shares/preview', { link }, f)
export const listMembers = (f?: typeof fetch) => call<{ members: MemberView[] }>('GET', '/api/data/members', undefined, f)
export const changeRole = (account: string, role: LabRole, f?: typeof fetch) => call<{ role: LabRole }>('POST', `/api/data/members/${enc(account)}`, { role }, f)
/** Show a private dataset to the meeting this browser presents; `acknowledged`: the presenter confirmed the notice (decision O6). */
export const showToMeeting = (dataset: string, room: string, st: string, f?: typeof fetch) => call<{ share: string; expiresAt: number }>('POST', `/api/data/datasets/${enc(dataset)}/meeting`, { room, st, acknowledged: true }, f)
export const stopShowingToMeeting = (dataset: string, room: string, f?: typeof fetch) => call<{ stopped: number }>('DELETE', `/api/data/datasets/${enc(dataset)}/meeting`, { room }, f)
export const openDatasetPublicly = (dataset: string, confirm: string, f?: typeof fetch) => call<{ open: string; url: string }>('POST', `/api/data/datasets/${enc(dataset)}/open`, { licence: 'CC-BY-4.0', confirm }, f)
export const closeDatasetPublicly = (dataset: string, f?: typeof fetch) => call<{ open: null }>('DELETE', `/api/data/datasets/${enc(dataset)}/open`, undefined, f)

/**
 * Ask for a step-up: passkey request options, or a mailed code (`devCode`
 * only from a local server). `lostPasskey`: a mailed code although the
 * account has passkeys, only to request a passkey's delayed removal.
 */
export const startStepUp = (action: StepUpAction, lostPasskey = false, f?: typeof fetch) =>
  call<{ required: boolean; method?: 'passkey' | 'email'; challenge?: string; expiresAt?: number; sentTo?: string; devCode?: string; publicKey?: RequestJson }>('POST', '/api/data/stepup', lostPasskey ? { action, lostPasskey: true } : { action }, f)
/** Prove a step-up with the emailed code, or with a passkey's assertion. */
export const verifyStepUp = (challenge: string, proof: { code: string } | { assertion: Record<string, unknown> }, f?: typeof fetch) =>
  call<{ verified: boolean; method: string; until: number }>('POST', '/api/data/stepup/verify', { challenge, ...proof }, f)

// ------------------------------------------------------------- passkeys (O4)

export interface PasskeyView {
  id: string
  name: string
  createdAt: number
  lastUsedAt: number | null
  synced: boolean
  algorithm: string
}

export interface PasskeyRemovalView { id: string; passkey: string; effectiveAt: number }
export const listPasskeys = (f?: typeof fetch) => call<{ passkeys: PasskeyView[]; removals: PasskeyRemovalView[] }>('GET', '/api/data/passkeys', undefined, f)
/** A lost passkey: ask for its removal in 72 hours (after an emailed code). */
export const requestPasskeyRemoval = (id: string, f?: typeof fetch) => call<{ removal: PasskeyRemovalView }>('POST', `/api/data/passkeys/${enc(id)}/removal`, {}, f)
/** Cancel a pending removal (needs a passkey). */
export const cancelPasskeyRemoval = (id: string, f?: typeof fetch) => call<{ cancelled: boolean }>('DELETE', `/api/data/passkeys/removals/${enc(id)}`, undefined, f)
/** The "this wasn't me" link from the passkey-added mail: removes that passkey and signs the account out everywhere. */
export const revokePasskeyByLink = (link: string, f?: typeof fetch) => call<{ removed: boolean; signedOut: boolean }>('POST', '/api/data/passkeys/revoke', { link }, f)
export const passkeyOptions = (f?: typeof fetch) => call<{ challenge: string; expiresAt: number; publicKey: CreationJson }>('POST', '/api/data/passkeys/options', {}, f)
export const registerPasskey = (challenge: string, name: string, credential: Record<string, unknown>, f?: typeof fetch) =>
  call<{ passkey: PasskeyView; devRevokeLink?: string }>('POST', '/api/data/passkeys', { challenge, name, credential }, f)
export const removePasskey = (id: string, f?: typeof fetch) => call<{ removed: boolean }>('DELETE', `/api/data/passkeys/${enc(id)}`, undefined, f)

// ----------------------------------------------- phase 3: continuous sources

export interface SourceView {
  id: string
  dataset: string
  title: string
  state: SourceState
  endpoint: string
  bucket: string
  prefix: string
  region: string
  keyHint: string | null
  schedule: { kind: 'poll'; minutes: number } | { kind: 'notify' }
  window: { kind: 'recent'; frames: number } | { kind: 'archive' }
  lastPollAt: number | null
  lastGoodRelease: string | null
  objectsSeen: number
  error: string | null
  createdAt: number
  mine: boolean
  alerts: Array<{ id: string; at: number; kind: SourceAlertKind | 'release_refused'; message: string | null; releaseKept: string | null }>
}

export interface NewSource {
  title: string
  endpoint: string
  bucket: string
  prefix: string
  region: string
  accessKeyId: string
  secretAccessKey: string
  schedule: SourceSchedule
  window: SourceWindow
  choices: JobChoices
}

export const listSources = (f?: typeof fetch) => call<{ sources: SourceView[]; quota: { sources: number; used: number } | null }>('GET', '/api/data/sources', undefined, f)
export const addSource = (body: NewSource, f?: typeof fetch) => call<{ source: SourceView; dispatched: boolean }>('POST', '/api/data/sources', body, f)
export const sourceAction = (id: string, action: 'pause' | 'resume' | 'clear-alerts', f?: typeof fetch) => call<{ source?: SourceView; cleared?: number }>('POST', `/api/data/sources/${enc(id)}/${action}`, {}, f)
export const deleteSource = (id: string, f?: typeof fetch) => call<{ deleted: boolean; intakeConfirmed: boolean }>('DELETE', `/api/data/sources/${enc(id)}`, undefined, f)

/** Job states that are still moving without the customer (the page polls them). */
export const MOVING_STATES: readonly JobState[] = ['uploading', 'uploaded', 'checking', 'inspecting', 'processing']

/** The steps the job page shows, in order. */
export const JOB_STEPS = ['checking', 'inspecting', 'awaiting_choices', 'processing', 'review', 'published'] as const

/** Where a job is on the steps: the index of its state, `uploaded` counting as before the first. */
export function jobStepIndex(state: JobState): number {
  if (state === 'uploading' || state === 'uploaded') return -1
  return (JOB_STEPS as readonly string[]).indexOf(state)
}
